// @flow

import React, { useRef, useCallback } from 'react';
import type { Node, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, SliderPagination, scrollToElement } from '@riseart/common';
import { GridHeader, GridCounter, GridSortSelect, GridPaginationWrapper } from '@riseart/grid';
import { HOCGridSort } from 'shared_hocs/filters/Sorting';
import { artist as ENUM_ARTIST, filter as ENUM_FILTER } from 'Enum';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { Pagination } from 'shared_hocs/pagination/Pagination';
import { SliderPaginationCounter } from 'shared_components/common/pagination/counter/Slider';
import { ArtistListGrid } from 'shared_components/artist/list/Grid';
import { FollowButton } from 'shared_components/common/follow/Button';
import { FilterFollowsProvider } from 'shared_data/providers/follow/Follow';

import { artistListContainerCls } from 'shared_components/artist/list/List.less';

type Props = {
  loading: boolean,
  items: Array<Object>,
  textSearchQuery?: string,
  pageUrl: string,
  paginationType: string,
  pagination: Object,
  domain: string,
  storeCode: string,
};

const ArtistSort = HOCGridSort(GridSortSelect, { domain: ENUM_FILTER.domain.ARTIST });

/**
 * infoListRenderer
 *
 * @param {Object} item
 * @returns {Array<any>}
 */
function infoListRenderer(item: Object): Array<any> {
  const infoList = [];

  if (item.livesCountry) {
    infoList.push(
      <FormattedMessage id={`countries.${item.livesCountry.toUpperCase()}`}>
        {(country: string) => country}
      </FormattedMessage>,
    );
  }

  if (item.birthYear) {
    infoList.push(
      <FormattedMessage id="common.birthdayAbbreviated">
        {(label: string) => `${label} ${item.birthYear}`}
      </FormattedMessage>,
    );
  }

  infoList.push(<FollowButton leaderId={item.id} leaderData={item} />);

  return infoList;
}

/**
 * ArtistList
 *
 * @param {Props} props
 * @returns Node
 */
export function ArtistList({
  domain,
  loading = false,
  items = [],
  pageUrl,
  paginationType,
  pagination: paginationProps,
  storeCode,
  textSearchQuery,
}: Props): Node {
  const GRID_SCROLL_TO_TOP_OFFSET = 50;
  const gridRef: RefObject<HTMLDivElement | null> = useRef(null);
  const handleScrollToTopOfGrid = useCallback(
    () => scrollToElement(gridRef, GRID_SCROLL_TO_TOP_OFFSET),
    [gridRef],
  );

  return (
    <IsomorphicRipple isActive={loading}>
      <div ref={gridRef} className={artistListContainerCls}>
        <GridHeader>
          <ArtistSort
            domain={domain}
            translationPath="options.sort.artist"
            sortList={
              textSearchQuery === undefined
                ? Object.keys(ENUM_ARTIST.rank).reduce((accumulator, sortEnumKey) => {
                    const value = ENUM_ARTIST.rank[sortEnumKey];

                    return value === ENUM_ARTIST.rank.RANKING_RELEVANCE
                      ? accumulator
                      : { ...accumulator, [sortEnumKey]: value };
                  }, {})
                : ENUM_ARTIST.rank
            }
          />
          {paginationProps.current ? (
            <div>
              <GridCounter>
                <SliderPaginationCounter
                  currentPage={paginationProps.current}
                  itemsPerPage={paginationProps.pageSize}
                  totalItems={paginationProps.total}
                />
              </GridCounter>
            </div>
          ) : null}
        </GridHeader>
        <FilterFollowsProvider>
          <ArtistListGrid
            renderFollow
            items={items}
            storeCode={storeCode}
            infoListRenderer={infoListRenderer}
            loading={loading}
            noItems={
              <FormattedMessage
                id="components.artist.gridNoArtists"
                values={{
                  p: (chunks) => <p>{chunks}</p>,
                  strong: (chunks) => <strong>{chunks}</strong>,
                  a: (chunks) => (
                    <FormattedMessage id="components.artist.gridNoArtistsLinkTitle">
                      {(linkTitle: string) => (
                        <Link
                          obfuscate
                          rel="nofollow"
                          to={UrlAssembler.artList({ search: { q: textSearchQuery || '' } })}
                          title={linkTitle}
                        >
                          {chunks}
                        </Link>
                      )}
                    </FormattedMessage>
                  ),
                }}
              >
                {(noItemsText: string) => noItemsText}
              </FormattedMessage>
            }
          />
        </FilterFollowsProvider>

        {items && items.length > 0 ? (
          <GridPaginationWrapper>
            <Pagination
              {...paginationProps}
              paginationType={paginationType}
              pageUrl={pageUrl}
              beforeChange={handleScrollToTopOfGrid}
            >
              {(props) => <SliderPagination {...props} />}
            </Pagination>
          </GridPaginationWrapper>
        ) : null}
      </div>
    </IsomorphicRipple>
  );
}
